import { useCallback, useEffect, useState } from "react";
import { Download, ShieldCheck, ShieldAlert, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { fmtFecha, ROLES } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { StatusBadge } from "./StatusBadge";
import { PageHeader } from "./PageHeader";

const LIMIT = 50;
const EMPTY = { desde: "", hasta: "", usuario: "", accion: "" };

const ChainBadge = ({ v }) =>
  !v ? null : v.valida ? (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border bg-cyan-500/10 text-cyan-700 border-cyan-300" data-testid="chain-status-valid">
      <ShieldCheck size={14} /> Cadena íntegra · {v.total} registros
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border bg-rose-500/10 text-rose-700 border-rose-300" data-testid="chain-status-invalid">
      <ShieldAlert size={14} /> Cadena alterada en registro #{v.error_en_seq}
    </span>
  );

const Filters = ({ f, setF, acciones, onSearch }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end bg-white border border-slate-200 rounded-xl p-4">
    {[["desde", "Desde"], ["hasta", "Hasta"]].map(([k, l]) => (
      <div key={k} className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate-500">{l}</Label>
        <Input type="date" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`audit-filter-${k}`} />
      </div>
    ))}
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">Usuario (correo)</Label>
      <Input value={f.usuario} placeholder="correo@empresa.cl" onChange={(e) => setF({ ...f, usuario: e.target.value })} data-testid="audit-filter-usuario" />
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">Acción</Label>
      <Select value={f.accion || "todas"} onValueChange={(v) => setF({ ...f, accion: v === "todas" ? "" : v })}>
        <SelectTrigger data-testid="audit-filter-accion"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="todas">Todas</SelectItem>
          {acciones.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <Button onClick={onSearch} className="bg-slate-900 hover:bg-slate-800" data-testid="audit-search-button">
      <Search size={16} className="mr-2" /> Filtrar
    </Button>
  </div>
);

const Rows = ({ items }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
    <Table data-testid="audit-table">
      <TableHeader>
        <TableRow>
          {["#", "Fecha (Santiago)", "Usuario", "Acción", "Entidad", "Resultado", "IP", "Hash"].map((h) => <TableHead key={h}>{h}</TableHead>)}
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((e) => (
          <TableRow key={e.id} data-testid={`audit-row-${e.seq}`}>
            <TableCell className="font-mono text-xs">{e.seq}</TableCell>
            <TableCell className="whitespace-nowrap text-sm">{fmtFecha(e.timestamp)}</TableCell>
            <TableCell className="text-sm">
              <div>{e.correo || "anónimo"}</div>
              <div className="text-xs text-slate-500">{ROLES[e.rol] || ""}</div>
            </TableCell>
            <TableCell className="font-mono text-xs">{e.accion}</TableCell>
            <TableCell className="text-xs text-slate-600">{e.entidad}</TableCell>
            <TableCell><StatusBadge value={e.resultado} label={e.resultado} /></TableCell>
            <TableCell className="font-mono text-xs">{e.ip}</TableCell>
            <TableCell><code className="font-mono text-[11px] bg-slate-100 text-cyan-700 px-1.5 py-0.5 rounded" title={e.hash}>{e.hash.slice(0, 12)}…</code></TableCell>
          </TableRow>
        ))}
        {!items.length && (
          <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">Sin registros para estos filtros</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  </div>
);

export const AuditView = ({ base, title }) => {
  const [f, setF] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [data, setData] = useState({ items: [], total: 0 });
  const [skip, setSkip] = useState(0);
  const [acciones, setAcciones] = useState([]);
  const [verify, setVerify] = useState(null);

  const params = (p) => Object.fromEntries(Object.entries(p).filter(([, v]) => v));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`${base}/audit`, { params: { ...params(applied), skip, limit: LIMIT } });
      setData(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [base, applied, skip]);

  const doVerify = useCallback(async () => {
    const { data } = await api.get(`${base}/audit/verify`);
    setVerify(data);
  }, [base]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get(`${base}/audit/acciones`).then((r) => setAcciones(r.data.items));
    doVerify();
  }, [base, doVerify]);

  const exportar = async () => {
    try {
      const r = await api.get(`${base}/audit/export`, { params: params(applied), responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "bitacora.csv";
      a.click();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-view">
      <PageHeader overline="Bitácora inmutable" title={title}>
        <ChainBadge v={verify} />
        <Button variant="outline" onClick={doVerify} data-testid="audit-verify-button">Verificar cadena</Button>
        <Button variant="outline" onClick={exportar} data-testid="audit-export-button"><Download size={16} className="mr-2" /> Exportar CSV</Button>
      </PageHeader>
      <Filters f={f} setF={setF} acciones={acciones} onSearch={() => { setSkip(0); setApplied(f); }} />
      <Rows items={data.items} />
      <div className="flex items-center justify-between text-sm text-slate-600">
        <span data-testid="audit-total">{data.total} registros</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} data-testid="audit-prev-page">Anterior</Button>
          <Button variant="outline" size="sm" disabled={skip + LIMIT >= data.total} onClick={() => setSkip(skip + LIMIT)} data-testid="audit-next-page">Siguiente</Button>
        </div>
      </div>
    </div>
  );
};
